import React, { useState } from "react";
import { format } from "d3-format";
import {
  TooltipBodyRow,
  TooltipBodyRowLabel,
  TooltipBodyRowValue,
  SectorLabelWithColorIcon,
  ShowMoreLessButton,
  TooltipExploreMoreContainer,
  TooltipExploreMoreButton,
  ProductCodeLabel,
} from "@atlas-int/viz/shared";
import { ProductClass, ProductLevel, TradeDirection, TradeFlow } from "@atlas-int/data/types";
import { formatTotalValue, formatAsPercentage, computeMonetaryValueByTradeFlow, worldGroupDatum, ColorBy, UIView, VizType } from "@atlas-int/data";
import { getColorMap } from "@atlas-int/viz";
import { complexityColorScale } from "@atlas-int/viz/treemap";
import { googleAnalyticsCustomEvent } from "../../../sharedUtilities/analytics";
import { vizTypeIcons } from "../../../assets/icons";
import { Tooltip } from "@atlas-int/viz/shared";
import { TreemapTooltipContent } from "./TreemapTooltipContent";

import type {
  TooltipProductDatum,
  LocationLookup,
  SetQueryFn,
  L10n,
  ProductMetadataByLevel,
} from "@atlas-int/viz/shared";

interface DefaultTooltipContentProps {
  datum: TooltipProductDatum;
  view: UIView;
  totalValue?: number;
  tradeDirection?: TradeDirection;
  productClass?: ProductClass;
  fillColor?: string;
  colorBySelection?: ColorBy;
  tradeFlow?: TradeFlow;
  product?: string;
  exporter?: string;
  importer?: string;
  setQuery?: SetQueryFn;
  l10n?: L10n;
  productMetadata?: ProductMetadataByLevel;
  locationLookup?: LocationLookup;
}

/**
 * Default tooltip content for markets/products that don't fall into other categories
 */
export const DefaultTooltipContent: React.FC<DefaultTooltipContentProps> = ({
  datum,
  view,
  totalValue,
  tradeDirection,
  productClass,
  fillColor: _fillColor,
  colorBySelection,
  tradeFlow,
  product,
  exporter = "",
  importer = "",
  setQuery,
  l10n,
  productMetadata,
  locationLookup,
}) => {
  const [showMore, setShowMore] = useState<boolean>(false);

  let netGrossLabelModifier;
  if (tradeFlow === TradeFlow.Gross) {
    netGrossLabelModifier = "Gross";
  } else if (tradeFlow === TradeFlow.Net) {
    netGrossLabelModifier = "Net";
  }

  if (view === UIView.Markets) {
    const { countryId, groupId, id } = datum;

    let valueToUse;

    if (tradeDirection === TradeDirection.Exports) {
      valueToUse = computeMonetaryValueByTradeFlow({
        datum,
        tradeFlow: tradeFlow,
        tradeDirection,
      });
    } else if (tradeDirection === TradeDirection.Imports) {
      valueToUse = computeMonetaryValueByTradeFlow({
        datum,
        tradeFlow: tradeFlow,
        tradeDirection,
      });
    }

    if (!valueToUse) {
      if ((datum?.properties as Record<string, string> | undefined)?.importsColor === "#e8c258") {
        return (
          <>
            <TooltipBodyRow>
              <TooltipBodyRowLabel>Selected Country</TooltipBodyRowLabel>
            </TooltipBodyRow>
          </>
        );
      } else {
        return (
          <>
            <TooltipBodyRow>
              <TooltipBodyRowLabel>No data available</TooltipBodyRowLabel>
            </TooltipBodyRow>
          </>
        );
      }
    } else {
      return (
        <TreemapTooltipContent
          datum={datum}
          totalValue={totalValue || 0}
          locationLookup={locationLookup!}
          colorBySelection={colorBySelection || ColorBy.Sector}
          tradeFlow={tradeFlow || TradeFlow.Gross}
          tradeDirection={tradeDirection || TradeDirection.Exports}
          exporter={exporter || ""}
          importer={importer || ""}
          setQuery={setQuery!}
          l10n={l10n!}
          view={view}
          productMetadata={productMetadata}
          productClass={productClass || ProductClass.SITCProducts}
          product={product || ""}
        />
      );
    }
  } else if (view === UIView.Products && productMetadata !== undefined) {
    const {
      productId,
      topLevelParent,
      productLevel,
      exportRca,
      distance,
      pci,
    } = datum;

    let colorMap = getColorMap({ view, productClass });
    let matchingFillColor = colorMap ? colorMap.get(topLevelParent) : undefined;
    let matchingSector = productMetadata
      ? productMetadata.section.find(
          (productSection) => productSection.productId === topLevelParent,
        )
      : undefined;

    let valueToUse;
    let labelToUse;

    if (tradeDirection === TradeDirection.Exports) {
      valueToUse = computeMonetaryValueByTradeFlow({
        datum,
        tradeFlow: tradeFlow,
        tradeDirection,
      });
      labelToUse = netGrossLabelModifier
        ? `${netGrossLabelModifier} Export Value`
        : "Export Value";
    } else if (tradeDirection === TradeDirection.Imports) {
      valueToUse = computeMonetaryValueByTradeFlow({
        datum,
        tradeFlow: tradeFlow,
        tradeDirection,
      });
      labelToUse = netGrossLabelModifier
        ? `${netGrossLabelModifier} Import Value`
        : "Import Value";
    }

    const toggleShowMore = () => setShowMore(!showMore);

    let sectorFillColor =
      colorBySelection === ColorBy.Sector
        ? matchingFillColor
        : undefined;
    let pciFillColor =
      colorBySelection === ColorBy.Complexity && VizType.Tree
        ? complexityColorScale(pci)
        : undefined;

    let pciContentRow = (
      <TooltipBodyRow>
        <TooltipBodyRowLabel>
          <Tooltip
            explanation={l10n?.getString("settings-pci-tooltipText")}
            title={l10n?.getString("settings-pci-title")}
            inlineTooltip={true}
          >
            {l10n?.getString("settings-pci-title")}
          </Tooltip>
        </TooltipBodyRowLabel>
        <TooltipBodyRowValue>
          <SectorLabelWithColorIcon $fillColor={pciFillColor}>
            {format(",.3f")(pci)}
          </SectorLabelWithColorIcon>
        </TooltipBodyRowValue>
      </TooltipBodyRow>
    );

    if (!showMore) {
      return (
        <>
          {matchingSector && (
            <TooltipBodyRow>
              <TooltipBodyRowLabel>Sector:</TooltipBodyRowLabel>
              <TooltipBodyRowValue>
                <SectorLabelWithColorIcon $fillColor={sectorFillColor}>
                  {matchingSector.nameShortEn}
                </SectorLabelWithColorIcon>
              </TooltipBodyRowValue>
            </TooltipBodyRow>
          )}
          <TooltipBodyRow>
            <TooltipBodyRowLabel>{labelToUse}:</TooltipBodyRowLabel>
            <TooltipBodyRowValue>
              ${formatTotalValue(valueToUse)}
            </TooltipBodyRowValue>
          </TooltipBodyRow>
          <TooltipBodyRow>
            <TooltipBodyRowLabel>Share:</TooltipBodyRowLabel>
            <TooltipBodyRowValue>
              {totalValue && formatAsPercentage(valueToUse / totalValue)}%
            </TooltipBodyRowValue>
          </TooltipBodyRow>
          {colorBySelection === ColorBy.Complexity
            ? pciContentRow
            : null}
          {productLevel === ProductLevel.Product4digit && (
            <TooltipBodyRow>
              <TooltipBodyRowLabel>
                <ShowMoreLessButton onClick={toggleShowMore}>
                  Show more »
                </ShowMoreLessButton>
              </TooltipBodyRowLabel>
            </TooltipBodyRow>
          )}
          <TooltipExploreMoreContainer>
            <h2>For this product explore:</h2>
            <TooltipExploreMoreButton
              icon={vizTypeIcons.treemap}
              onClick={() => {
                googleAnalyticsCustomEvent(
                  "tooltipbutton",
                  "click",
                  "1",
                  "explore",
                );
                if (setQuery) {
                  setQuery({
                    view: "markets",
                    importer: worldGroupDatum.groupId,
                    exporter: worldGroupDatum.groupId,
                    tradeDirection: "exports",
                    product: productId,
                    colorBy: undefined,
                  });
                }
              }}
            >
              Where did World export this product to?
            </TooltipExploreMoreButton>

            <TooltipExploreMoreButton
              icon={vizTypeIcons.treemap}
              onClick={() => {
                googleAnalyticsCustomEvent(
                  "tooltipbutton",
                  "click",
                  "2",
                  "explore",
                );
                if (setQuery) {
                  setQuery({
                    view: "markets",
                    exporter: worldGroupDatum.groupId,
                    importer: worldGroupDatum.groupId,
                    tradeDirection: "imports",
                    product: productId,
                    colorBy: undefined,
                  });
                }
              }}
            >
              Where did World export this product to?
            </TooltipExploreMoreButton>
          </TooltipExploreMoreContainer>
        </>
      );
    } else if (showMore) {
      return (
        <>
          <TooltipBodyRow>
            <TooltipBodyRowLabel>
              <Tooltip
                explanation={l10n?.getString("settings-rca-tooltipText")}
                title={l10n?.getString("settings-rca-title")}
                inlineTooltip={true}
              >
                {l10n?.getString("settings-rca-title")}
              </Tooltip>
            </TooltipBodyRowLabel>
            <TooltipBodyRowValue>
              {format(",.2f")(exportRca)}
            </TooltipBodyRowValue>
          </TooltipBodyRow>
          <TooltipBodyRow>
            <TooltipBodyRowLabel>Distance:</TooltipBodyRowLabel>
            <TooltipBodyRowValue>
              {format(",.3f")(distance)}
            </TooltipBodyRowValue>
          </TooltipBodyRow>
          {colorBySelection === ColorBy.Sector ? pciContentRow : null}
          <TooltipBodyRow>
            <TooltipBodyRowLabel>
              <ShowMoreLessButton onClick={toggleShowMore}>
                Show less «
              </ShowMoreLessButton>
            </TooltipBodyRowLabel>
          </TooltipBodyRow>
        </>
      );
    }
  }

  return null;
};

/**
 * Gets the tooltip header for default tooltip content
 */
export const getDefaultTooltipHeader = (
  datum: TooltipProductDatum,
  view: UIView,
  productClass?: ProductClass,
  _productMetadata?: ProductMetadataByLevel,
): React.ReactNode => {
  if (view === UIView.Markets) {
    const { nameEn, groupName } = datum;
    if (groupName) {
      return groupName;
    } else {
      return nameEn;
    }
  } else if (view === UIView.Products) {
    const { nameEn, productCode } = datum;
    return (
      <>
        {nameEn + " "}
        <ProductCodeLabel>
          | {productCode} {productClass}
        </ProductCodeLabel>
      </>
    );
  }

  return null;
};
